<script>
import _ from 'lodash'
import { Bar } from 'vue-chartjs'
import { mapGetters } from 'vuex'

export default {
  extends: Bar,
  props: {
    userPerformances: { type: Array, default: () => [] },
    label1: { type: String, default: 'Staked Models' },
    label2: { type: String, default: 'All Models' },
    data1: { type: Array, default: () => [] }, // Staked models
    data2: { type: Array, default: () => [] }, // All models
    height: { type: String, default: '' },
    suggestedMin: { type: Number, default: 0 },
    suggestedMax: { type: Number, default: 50 },
    yTicksLimit: { type: Number, default: 8 },
    xTicksLimit: { type: Number, default: 6 },
    invertedYAxis: { type: Boolean, default: false },
    showTooltip: { type: Boolean, default: true },
    yAxisMin: { type: Number, default: null },
    labels: { type: Array, default: () => [] },
    legend: { type: Boolean, default: true },
    tc: { type: Boolean, default: false },
    minimalist: { type: Boolean, default: false },
    legendPlacement: { type: String, default: 'top' },
  },

  computed: {
    ...mapGetters(['isDarkMode']),
    stepSize() {
      const maxYValue = Math.max(this.data1.concat(this.data2))
      return maxYValue > 300
        ? 100
        : maxYValue > 150
        ? 50
        : maxYValue > 75
        ? 20
        : 10
    },
    xAxes() {
      return [
        {
          stacked: true,
          type: 'category',
          // Use 2 decimals of precision for xAxis tick marks
          labels: this.labels.map(x => Math.round(x * 100) / 100),
          ticks: {
            autoSkip: false,
            padding: 10,
            fontSize: 10.5,
            fontColor: '#999999',
            autoSkipPadding: 10,
            // Skip odd labels so they aren't crammed
            callback: function(value, index) {
              return index % 3 ? '' : value
            },
          },
          gridLines: {
            drawTicks: false,
            color: '#fff0',
            zeroLineColor: '#fff0',
          },
        },
      ]
    },
    chartPrimaryColour() {
      if (this.isDarkMode) {
        return '#FFF'
      } else {
        return '#000'
      }
    },
    barColor() {
      return this.tc ? '#A278DC' : '#2196f3'
    },
  },
  watch: {
    data1() {
      this.render()
    },
    data2() {
      this.render()
    },
    isDarkMode() {
      this.render()
    },
    userPerformances() {
      this.render()
    },
  },
  mounted() {
    this.render()
  },
  methods: {
    userPerformancePoint(score) {
      // Find the bucket that userPerformance belongs to
      const bucketIndex =
        this.labels.toSorted((a, b) => a - b).findIndex(x => score.value <= x) -
        0
      return [
        {
          x: Math.round(this.labels[bucketIndex] * 100) / 100,
          y: this.data2[bucketIndex],
        },
      ]
    },
    render() {
      const data = {
        labels: this.labels,
        userPerformances: this.userPerformances,
        datasets: [
          {
            borderColor: this.isDarkMode ? '#000' : '#FFF',
            pointStyle: 'rect',
            label: this.label1,
            data: this.data1,
            fill: false,
            pointRadius: 0.5,
            lineTension: 0,
            borderWidth: 0.2,
            backgroundColor: this.barColor,
            barPercentage: 1,
          },
          {
            borderColor: this.isDarkMode ? '#000' : '#FFF',
            label: this.label2,
            data: this.data2,
            fill: false,
            pointRadius: 0.5,
            lineTension: 0,
            borderWidth: 0.2,
            backgroundColor: this.isDarkMode ? '#555' : '#ccc',
            barPercentage: 1,
          },
        ],
      }
      for (const score of this.userPerformances) {
        data.datasets.push({
          type: 'line',
          data: this.userPerformancePoint(score),
          label: 'Your Models',
          pointRadius: 6,
          backgroundColor: this.isDarkMode ? '#fff' : '#000',
          pointBorderColor: this.isDarkMode ? '#000' : '#fff',
          pointBorderWidth: 0,
          pointBackgroundColor: this.isDarkMode ? '#fff' : '#000',
          pointStyle: 'rectRot',
          order: -1, // Render on top
        })
      }

      if (this.minimalist) {
        data.datasets = data.datasets.slice(0, 2)
      }

      const tooltipLabel = modelName => {
        return this.tc ? `${modelName} TC: ` : `${modelName} MMC: `
      }

      // adding this variable so we can use it below in the tooltip since
      // 'this' is the ChartElement not props. ( so previous this.tc was undefined )
      // there must be a better way to do this but im  redacted
      let tc = this.tc

      var options = {
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
          },
        },
        animation: {
          duration: 0,
        },
        legend: {
          display: this.legend,
          position: this.legendPlacement,
          labels: {
            boxWidth: 12,
            usePointStyle: true,
            generateLabels: function(chart) {
              // Get default labels
              // eslint-disable-next-line no-undef
              const labels = Chart.defaults.global.legend.labels.generateLabels(
                chart,
              )

              // Filter labels to hide duplicates
              return labels.filter((obj, pos, arr) => {
                return arr.map(mapObj => mapObj.text).indexOf(obj.text) === pos
              })
            },
          },
          // Prevent dataset hiding by user
          onClick: e => e.stopPropagation(),
        },
        scales: {
          xAxes: this.xAxes,
          yAxes: [
            {
              ticks: {
                reverse: this.invertedYAxis,
                stepSize: this.stepSize,
                scale: 0.02,
                suggestedMin: this.suggestedMin,
                suggestedMax: this.suggestedMax,
                maxTicksLimit: this.yTicksLimit,
                padding: 10,
                fontSize: 10.5,
                fontColor: '#999999',
                display: !this.minimalist,
              },
              gridLines: {
                color: this.isDarkMode ? '#20202b' : '#eaeaea',
                drawTicks: false,
                drawBorder: false,
                display: !this.minimalist,
              },
            },
          ],
        },
        tooltips: {
          enabled: true,
          intersect: false,
          cornerRadius: 2,
          mode: 'x',
          position: 'nearest',
          callbacks: {
            label: function(tooltipItem, data) {
              // Third dataset is user performance
              if (tooltipItem.datasetIndex >= 2) {
                const score =
                  data.userPerformances[tooltipItem.datasetIndex - 2]
                return (
                  tooltipLabel(score.modelName) +
                  Math.round(score.value * 1000) / 1000
                )
              }

              return (
                data.datasets[tooltipItem.datasetIndex].label +
                ': ' +
                tooltipItem.value
              )
            },
            title: function(tooltipItem, data) {
              // Use 3 digits of precision for tooltip titles
              const title = tc ? 'TC ' : 'CORR20 + MMC20 '
              return (
                title +
                Math.round(data.labels[tooltipItem[0].index] * 1000) / 1000
              )
            },
          },
        },
      }

      if (this.yAxisMin != null) {
        _.set(options, 'scales.yAxes[0].ticks.min', this.yAxisMin)
        _.set(options, 'scales.yAxes[0].gridLines.zeroLineWidth', 0.5)
      }

      this.renderChart(data, options)
    },
  },
}
</script>
